import { AfterViewInit, Component, Input, NgZone, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { SignInService } from '../../../common/services/sign-in/sign-in.service';
import GoogleUser = gapi.auth2.GoogleUser;
import { LmTitleService } from '../../../common/services/title.service';
import { ConfigService } from '../../../shared/config.service';
import { Config } from '../../../shared/config';
import { LottoUser } from './user.model';

@Component({
  selector: 'app-sign-in',
  templateUrl: './sign-in.component.html',
  styleUrls: ['./sign-in.component.scss'],
  standalone: false,
})
export class SignInComponent implements OnInit, AfterViewInit {
  @Input() loading: boolean = false;

  private auth2: gapi.auth2.GoogleAuth;

  public user: GoogleUser;

  constructor(
    private signInService: SignInService,
    private router: Router,
    private route: ActivatedRoute,
    private zone: NgZone,
    private lmTitleService: LmTitleService,
    private configService: ConfigService
  ) {}

  ngOnInit(): void {
    this.lmTitleService.setTitleSuffix('Sign in');
  }

  ngAfterViewInit(): void {
    const query = new URLSearchParams(location.search);
    switch (query.get('auth')) {
      case 'test': {
        this.signInService.setCurrentUser(this.createTestLottoUser(), true);
        break;
      }
      default: {
        const googleInit = (googleSsoClientId: string) => this.googleInit(googleSsoClientId);

        this.configService.getConfig().subscribe({
          next(config: Config) {
            this.googleSsoClientId = config.googleSsoClientId;
          },
          complete() {
            googleInit(this.googleSsoClientId);
          },
        });
      }
    }
  }

  public googleInit(googleSsoClientId: string): void {
    gapi.load('auth2', () => {
      this.auth2 = gapi.auth2.init({
        client_id: googleSsoClientId,
        cookie_policy: 'single_host_origin',
        scope: 'profile email',
      });
      gapi.signin2.render('my-signin2', {
        scope: 'profile email',
        width: 240,
        height: 50,
        longtitle: true,
        theme: 'dark',
        onsuccess: () => this.onsuccess(),
        onfailure: () => {
          console.error('FAILURE ON LOGGING');
        },
      });

      this.auth2.currentUser.listen((u: GoogleUser) => {
        this.signInService.setCurrentUser(this.createLottoUser(u));
      });

      this.auth2.isSignedIn.listen(() => {
        this.signInService.setCurrentUser(this.createLottoUser(this.auth2.currentUser.get()), true);
      });
    });
  }

  public onsuccess() {
    this.zone.run(() => {
      const { url } = this.router;

      if (url.startsWith('/sso')) {
        const { redirect } = this.route.snapshot.queryParams;
        const redirectUrl = redirect || '/';
        this.router.navigateByUrl(redirectUrl);
      }
    });
  }

  private createLottoUser(u: gapi.auth2.GoogleUser): LottoUser {
    return {
      id: u.getId(),
      getToken: () => u.getAuthResponse().id_token,
      email: u.getBasicProfile().getEmail(),
      imageUrl: u.getBasicProfile().getImageUrl(),
      name: u.getBasicProfile().getName(),
      isSignedIn: () => u.isSignedIn(),
      signOut: () => this.auth2.signOut(),
      getAuthHeaders: () => ({
        access_token: u.getAuthResponse().id_token,
      }),
    };
  }

  private createTestLottoUser(): LottoUser {
    const query = new URLSearchParams(location.search);
    return {
      id: '321312312',
      getToken: () => query.get('test-token'),
      email: query.get('test-email'),
      imageUrl: null,
      name: 'Test User',
      isSignedIn: () => true,
      signOut: () => this.auth2.signOut(),
      getAuthHeaders: () => ({
        'qa.user': query.get('test-email'),
        'qa.token': query.get('test-token'),
      }),
    };
  }
}
